/**
 * Help Scout Beacon (support chat bubble).
 *
 * The official embed is an inline <script> stub plus a loader. Our CSP blocks
 * inline scripts without a nonce, so the same stub lives here in the bundle and
 * the loader is added as an external <script src>, which script-src allows.
 *
 * The bubble is hidden (Beacon('destroy')) while any "suppressor" is active:
 * a live / joining session on the TransactionPage, an in-call view, the
 * Join popup, or a session banner on mobile. It comes back (Beacon('init'))
 * once every suppressor is released.
 */

export const HELP_SCOUT_BEACON_ID = '57158e6b-e959-4f95-8aa7-0cb9c41eea63';
export const HELP_SCOUT_BEACON_SRC = 'https://beacon-v2.helpscout.net';

// Below session modals (z-index 80-90) and app modals (100+), above the topbar.
export const HELP_SCOUT_BEACON_Z_INDEX = 70;
// Mobile: sit above the fixed bottom action bars (Book / Accept / Join, ~90px).
export const HELP_SCOUT_BEACON_MOBILE_OFFSET = 104;
// Sam, Oct 3 2026: the bubble says what it is.
export const HELP_SCOUT_BEACON_STYLE = 'iconAndText';
export const HELP_SCOUT_BEACON_TEXT = 'Help & Support';
// Sam, Oct 6 2026: on phones, form / checkout / session pages get a small
// round icon-only button tucked in the bottom-right corner (the labelled pill
// covered "Forgot password?" on /login). The page gets bottom padding while
// compact (see marketplaceDefaults.css, html[data-sl-beacon-compact='on']).
export const HELP_SCOUT_BEACON_COMPACT_STYLE = 'icon';
export const HELP_SCOUT_BEACON_COMPACT_ICON = 'message';
export const HELP_SCOUT_BEACON_COMPACT_OFFSET = 12;

const REINIT_DELAY_MS = 400;
// Cover guard: fade the bubble out while it sits on a field / button / link.
const GUARD_INTERVAL_MS = 600;
const FAB_SELECTOR = '.BeaconFabButtonFrame';
const INTERACTIVE_SELECTOR =
  'input, textarea, select, button, iframe, a[href], label, [role="button"], [role="link"], [contenteditable="true"], p, li, h1, h2, h3, legend';

const state = {
  enabled: false,
  initialized: false,
  scriptRequested: false,
  suppressors: new Map(),
  compact: false,
  initializedCompact: false,
  reinitTimer: null,
  nextToken: 1,
  beaconOpen: false,
  covering: false,
  guardTimer: null,
  guardFrame: null,
  guardListening: false,
};

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const markDom = () => {
  if (!hasWindow() || !document.documentElement) {
    return;
  }
  const el = document.documentElement;
  el.setAttribute('data-sl-beacon', state.initialized ? 'on' : 'off');
  const compactShown = state.compact && state.enabled && state.suppressors.size === 0;
  el.setAttribute('data-sl-beacon-compact', compactShown ? 'on' : 'off');
  const reasons = Array.from(new Set(state.suppressors.values()));
  if (reasons.length > 0) {
    el.setAttribute('data-sl-beacon-hidden-by', reasons.join(' '));
  } else {
    el.removeAttribute('data-sl-beacon-hidden-by');
  }
};

/**
 * Same queueing stub as Help Scout's embed: calls made before the loader
 * finishes are pushed to readyQueue and replayed by the real Beacon.
 */
const ensureBeaconStub = () => {
  if (typeof window.Beacon === 'function') {
    return;
  }
  const stub = function beaconStub(method, options, data) {
    stub.readyQueue.push({ method, options, data });
  };
  stub.readyQueue = [];
  window.Beacon = stub;
};

const appendLoader = () => {
  if (state.scriptRequested) {
    return;
  }
  state.scriptRequested = true;
  const insert = () => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = HELP_SCOUT_BEACON_SRC;
    script.setAttribute('data-sl-beacon-loader', 'true');
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }
  };
  if (document.readyState === 'complete') {
    insert();
  } else {
    window.addEventListener('load', insert, { once: true });
  }
};

const callBeacon = (...args) => {
  try {
    window.Beacon(...args);
  } catch (e) {
    // Never let the support widget break the page.
  }
};

const setCovering = covering => {
  if (state.covering === covering || !hasWindow()) {
    return;
  }
  state.covering = covering;
  if (covering) {
    document.documentElement.setAttribute('data-sl-beacon-covering', 'on');
  } else {
    document.documentElement.removeAttribute('data-sl-beacon-covering');
  }
};

/**
 * True when the closed bubble sits on top of something you can tap or type in.
 * Samples the bubble's corners + centre with elementsFromPoint.
 */
// Extra clearance (px) around the bubble that still counts as "covering".
const COVER_MARGIN = 4;
// How far inside a field's box to hit-test (clears rounded corners).
const CORNER_NUDGE = 6;

// A <label> is often a full-width block; only its text counts, not the empty space.
const labelTextRects = label => {
  if (!document.createRange) {
    return [label.getBoundingClientRect()];
  }
  try {
    const range = document.createRange();
    range.selectNodeContents(label);
    const rects = Array.from(range.getClientRects ? range.getClientRects() : []);
    return rects.length ? rects : [label.getBoundingClientRect()];
  } catch (e) {
    return [label.getBoundingClientRect()];
  }
};

// Points of rect q that sit under the bubble (empty when q is clear of it).
// Nudged inward so rounded field corners still hit-test as the field.
const touchPoints = (q, box) => {
  if (!q || !q.width || !q.height) {
    return [];
  }
  const { left, top, right, bottom, round, cx, cy, radius } = box;
  if (q.right <= left || q.left >= right || q.bottom <= top || q.top >= bottom) {
    return [];
  }
  const nx = Math.min(CORNER_NUDGE, q.width / 2);
  const ny = Math.min(CORNER_NUDGE, q.height / 2);
  const il = Math.max(q.left, left);
  const ir = Math.min(q.right, right);
  const it = Math.max(q.top, top);
  const ib = Math.min(q.bottom, bottom);
  const nearX = Math.max(q.left, Math.min(cx, q.right));
  const nearY = Math.max(q.top, Math.min(cy, q.bottom));
  const inX = x => Math.min(Math.max(x, q.left + nx), q.right - nx);
  const inY = y => Math.min(Math.max(y, q.top + ny), q.bottom - ny);
  const pts = [
    [
      Math.min(Math.max(nearX, q.left + 1), q.right - 1),
      Math.min(Math.max(nearY, q.top + 1), q.bottom - 1),
    ],
    [inX(nearX), inY(nearY)],
    [inX((il + ir) / 2), inY((it + ib) / 2)],
  ];
  return pts.filter(
    ([x, y]) =>
      x >= left &&
      x <= right &&
      y >= top &&
      y <= bottom &&
      (!round || Math.hypot(x - cx, y - cy) <= radius)
  );
};

// Really rendered at that point (not clipped, scrolled away, or hidden)?
const renderedAt = (el, fab, [x, y]) => {
  const vw = window.innerWidth || document.documentElement.clientWidth || 0;
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  if (x < 0 || y < 0 || (vw && x >= vw) || (vh && y >= vh)) {
    return false;
  }
  if (typeof document.elementsFromPoint !== 'function') {
    return true;
  }
  return document
    .elementsFromPoint(x, y)
    .some(hit => hit !== fab && (hit === el || (el.contains && el.contains(hit))));
};

/**
 * True when the floating bubble (plus a small margin) sits on top of a field,
 * button, or link that is actually visible there. The round icon-only bubble
 * ignores the empty corners of its box.
 */
export const beaconCoversInteractive = () => {
  if (!hasWindow()) {
    return false;
  }
  const fab = document.querySelector(FAB_SELECTOR);
  if (!fab) {
    return false;
  }
  const r = fab.getBoundingClientRect();
  if (!r.width || !r.height) {
    return false;
  }
  const box = {
    left: r.left - COVER_MARGIN,
    top: r.top - COVER_MARGIN,
    right: r.right + COVER_MARGIN,
    bottom: r.bottom + COVER_MARGIN,
    round: Math.abs(r.width - r.height) < 4,
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
    radius: r.width / 2 + COVER_MARGIN,
  };
  const candidates = document.querySelectorAll(INTERACTIVE_SELECTOR);
  for (let i = 0; i < candidates.length; i += 1) {
    const el = candidates[i];
    if (el.closest('#beacon-container') || el.closest(FAB_SELECTOR)) {
      continue;
    }
    const isBareLabel =
      el.tagName === 'LABEL' && !el.querySelector('input, select, textarea, button');
    const rects = isBareLabel ? labelTextRects(el) : [el.getBoundingClientRect()];
    for (let j = 0; j < rects.length; j += 1) {
      if (touchPoints(rects[j], box).some(pt => renderedAt(el, fab, pt))) {
        return true;
      }
    }
  }
  return false;
};

const checkCover = () => {
  state.guardFrame = null;
  if (!state.initialized || state.beaconOpen) {
    setCovering(false);
    return;
  }
  try {
    setCovering(beaconCoversInteractive());
  } catch (e) {
    setCovering(false);
  }
};

const scheduleCoverCheck = () => {
  if (!hasWindow() || state.guardFrame) {
    return;
  }
  const raf = window.requestAnimationFrame || (cb => window.setTimeout(cb, 16));
  state.guardFrame = raf(checkCover);
};

const startCoverGuard = () => {
  if (!hasWindow() || state.guardListening) {
    return;
  }
  state.guardListening = true;
  window.addEventListener('scroll', scheduleCoverCheck, { capture: true, passive: true });
  window.addEventListener('resize', scheduleCoverCheck, { passive: true });
  state.guardTimer = window.setInterval(scheduleCoverCheck, GUARD_INTERVAL_MS);
};

const stopCoverGuard = () => {
  if (!hasWindow() || !state.guardListening) {
    return;
  }
  state.guardListening = false;
  window.removeEventListener('scroll', scheduleCoverCheck, { capture: true });
  window.removeEventListener('resize', scheduleCoverCheck);
  if (state.guardTimer) {
    window.clearInterval(state.guardTimer);
    state.guardTimer = null;
  }
  setCovering(false);
};

/**
 * Beacon display config. Compact = icon-only round button low in the corner.
 *
 * @param {boolean} compact
 * @returns {Object}
 */
// Sam, Oct 21: on phones always the small round icon (the pill covered the home hero line).
const isPhone = () => typeof window !== 'undefined' && window.innerWidth < 768;
export const beaconDisplay = compact =>
  compact || isPhone()
    ? {
        style: HELP_SCOUT_BEACON_COMPACT_STYLE,
        iconImage: HELP_SCOUT_BEACON_COMPACT_ICON,
        position: 'right',
        zIndex: HELP_SCOUT_BEACON_Z_INDEX,
        verticalMobileOffset: HELP_SCOUT_BEACON_COMPACT_OFFSET,
        horizontalMobileOffset: HELP_SCOUT_BEACON_COMPACT_OFFSET,
      }
    : {
        style: HELP_SCOUT_BEACON_STYLE,
        text: HELP_SCOUT_BEACON_TEXT,
        zIndex: HELP_SCOUT_BEACON_Z_INDEX,
        verticalMobileOffset: HELP_SCOUT_BEACON_MOBILE_OFFSET,
      };

const doInit = () => {
  ensureBeaconStub();
  appendLoader();
  callBeacon('init', HELP_SCOUT_BEACON_ID);
  callBeacon('config', { display: beaconDisplay(state.compact) });
  // On-site answers (Sam, Oct 15). Search results inside the Beacon come from Help Scout's dashboard;
  // these suggestion links keep topic questions on Sweet Listeners.
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const SL_TOPIC_LINKS = [
    { text: 'You can search or browse topics like sports, shows, career, relationships and more. Search your own topic', url: origin + '/s?mode=search' },
    { text: 'Browse suggested topics', url: origin + '/s?mode=topics' },
    { text: 'Meet our listeners', url: origin + '/s?mode=listeners' },
  ];
  const SL_HELP_LINKS = [
    { text: 'FAQ', url: origin + '/faq' },
    { text: 'Help Center', url: origin + '/help' },
    { text: 'Contact us', url: origin + '/contact' },
  ];
  callBeacon('suggest', [...SL_TOPIC_LINKS, ...SL_HELP_LINKS]);
  callBeacon('on', 'search', data => {
    const q = String((data && data.query) || '').toLowerCase();
    callBeacon('suggest', /topic|search|browse|listener|talk about|subject/.test(q) ? [...SL_TOPIC_LINKS, ...SL_HELP_LINKS] : [...SL_HELP_LINKS, ...SL_TOPIC_LINKS]);
  });
  // Never fade the bubble while its chat window is open.
  callBeacon('on', 'open', () => {
    state.beaconOpen = true;
    setCovering(false);
  });
  callBeacon('on', 'close', () => {
    state.beaconOpen = false;
    scheduleCoverCheck();
  });
  state.initialized = true;
  startCoverGuard();
  state.initializedCompact = state.compact;
};

const doDestroy = () => {
  if (typeof window.Beacon === 'function') {
    callBeacon('close');
    callBeacon('destroy');
  }
  state.initialized = false;
  state.beaconOpen = false;
  stopCoverGuard();
};

const sync = () => {
  if (!hasWindow()) {
    return;
  }
  const want = state.enabled && state.suppressors.size === 0;
  if (!want) {
    if (state.reinitTimer) {
      window.clearTimeout(state.reinitTimer);
      state.reinitTimer = null;
    }
    if (state.initialized) {
      doDestroy();
    }
    markDom();
    return;
  }
  if (state.initialized && state.initializedCompact !== state.compact) {
    // Display style changed (e.g. /login -> /about on a phone). Beacon only
    // reads display on init, so rebuild it with the new style.
    doDestroy();
  }
  if (state.initialized || state.reinitTimer) {
    markDom();
    return;
  }
  // Short delay so a route change (session page -> session page) does not
  // flash the bubble between one suppressor releasing and the next mounting.
  state.reinitTimer = window.setTimeout(() => {
    state.reinitTimer = null;
    if (state.enabled && state.suppressors.size === 0 && !state.initialized) {
      doInit();
    }
    markDom();
  }, REINIT_DELAY_MS);
  markDom();
};

/** Turn the Beacon on for this page load (client only). */
export const enableHelpScoutBeacon = () => {
  state.enabled = true;
  sync();
};

/** Turn the Beacon off completely (e.g. app unmount / tests). */
export const disableHelpScoutBeacon = () => {
  state.enabled = false;
  sync();
};

/**
 * Hide the Beacon until the returned release function is called.
 * @param {string} reason short label, shown in data-sl-beacon-hidden-by
 * @returns {() => void} release
 */
export const suppressHelpScoutBeacon = (reason = 'session') => {
  const token = state.nextToken++;
  state.suppressors.set(token, reason);
  sync();
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    state.suppressors.delete(token);
    sync();
  };
};

/**
 * Icon-only compact bubble (phones on form / checkout / session pages).
 * @param {boolean} compact
 */
export const setHelpScoutBeaconCompact = compact => {
  const next = !!compact;
  if (state.compact === next) {
    return;
  }
  state.compact = next;
  sync();
};

export const helpScoutBeaconState = () => ({
  enabled: state.enabled,
  initialized: state.initialized,
  compact: state.compact,
  suppressed: state.suppressors.size > 0,
  reasons: Array.from(state.suppressors.values()),
});

// Test helper.
export const __resetHelpScoutBeaconForTests = () => {
  if (state.reinitTimer && hasWindow()) {
    window.clearTimeout(state.reinitTimer);
  }
  state.enabled = false;
  state.initialized = false;
  state.scriptRequested = false;
  state.suppressors = new Map();
  state.compact = false;
  state.initializedCompact = false;
  state.reinitTimer = null;
  stopCoverGuard();
  state.beaconOpen = false;
};

/** Small Help links: open the Beacon if it's loaded, else go to /help. */
export const openHelpWidget = e => {
  if (typeof window !== 'undefined' && typeof window.Beacon === 'function' && state.initialized) {
    if (e && e.preventDefault) e.preventDefault();
    window.Beacon('open');
    return true;
  }
  return false;
};
